import { Arrays, asInstanceOf, assert, ByteArrayInput, clamp, isDefined, isInstanceOf, Option, SetMultimap, UUID } from "@opendaw/lib-std";
import { AudioUnitBox, AuxSendBox, TrackBox } from "@opendaw/studio-boxes";
import { IndexedBox, PointerField } from "@opendaw/lib-box";
import { UnionBoxTypes } from "../unions";
import { AudioUnitOrdering } from "../factories/AudioUnitOrdering";
export var TransferUtils;
(function (TransferUtils) {
    const isSameGraph = ({ graph: a }, { graph: b }) => a === b;
    const compareIndex = (a, b) => a.index.getValue() - b.index.getValue();
    TransferUtils.excludeTimelinePredicate = (box) => box.accept({ visitTrackBox: () => true }) === true;
    TransferUtils.shouldExclude = (box) => box.ephemeral || box.name === AuxSendBox.ClassName;
    TransferUtils.generateMap = (audioUnitBoxes, dependencies, rootBoxUUID, masterBusBoxUUID) => {
        const uuidMap = UUID.newSet(({ source }) => source);
        uuidMap.addMany([
            ...audioUnitBoxes
                .filter(({ output: { targetAddress } }) => targetAddress.nonEmpty())
                .map(box => ({
                source: box.output.targetAddress.unwrap().uuid,
                target: masterBusBoxUUID
            })),
            ...audioUnitBoxes
                .map(box => ({
                source: box.collection.targetAddress.unwrap("AudioUnitBox was not connected to a RootBox").uuid,
                target: rootBoxUUID
            })),
            ...audioUnitBoxes
                .map(box => ({
                source: box.address.uuid,
                target: UUID.generate()
            })),
            ...dependencies
                .map(box => ({
                source: box.address.uuid,
                target: box.resource === "preserved" ? box.address.uuid : UUID.generate()
            }))
        ]);
        return uuidMap;
    };
    TransferUtils.copyBoxes = (uuidMap, targetBoxGraph, audioUnitBoxes, dependencies) => {
        const existingPreservedUuids = UUID.newSet(uuid => uuid);
        dependencies.forEach((source) => {
            if (source.resource === "preserved" && targetBoxGraph.findBox(source.address.uuid).nonEmpty()) {
                existingPreservedUuids.add(source.address.uuid);
            }
        });
        const isOwnedByExistingPreserved = (box) => {
            for (const [pointer, targetAddress] of box.outgoingEdges()) {
                if (pointer.mandatory && !targetAddress.isBox()) {
                    if (existingPreservedUuids.hasKey(targetAddress.uuid)) {
                        return true;
                    }
                }
            }
            return false;
        };
        PointerField.decodeWith({
            map: (_pointer, address) => address.flatMap(addr => uuidMap.opt(addr.uuid).match({
                some: ({ target }) => Option.wrap(addr.moveTo(target)),
                none: () => targetBoxGraph.findBox(addr.uuid).nonEmpty() ? Option.wrap(addr) : Option.None
            }))
        }, () => {
            audioUnitBoxes.forEach((source) => {
                const input = new ByteArrayInput(source.toArrayBuffer());
                const uuid = uuidMap.get(source.address.uuid).target;
                targetBoxGraph.createBox(source.name, uuid, box => box.read(input));
            });
            dependencies.forEach((source) => {
                if (existingPreservedUuids.hasKey(source.address.uuid)) {
                    return;
                }
                if (isOwnedByExistingPreserved(source)) {
                    return;
                }
                const input = new ByteArrayInput(source.toArrayBuffer());
                const uuid = uuidMap.get(source.address.uuid).target;
                targetBoxGraph.createBox(source.name, uuid, box => box.read(input));
            });
        });
    };
    TransferUtils.reorderAudioUnits = (uuidMap, audioUnitBoxes, rootBox, insertIndex) => {
        const targets = audioUnitBoxes
            .toSorted(compareIndex)
            .map(source => asInstanceOf(rootBox.graph
            .findBox(uuidMap.get(source.address.uuid).target)
            .unwrap("Target AudioUnit has not been copied"), AudioUnitBox));
        const targetSet = new Set(targets);
        const allAudioUnits = IndexedBox.collectIndexedBoxes(rootBox.audioUnits, AudioUnitBox);
        const existing = allAudioUnits.filter(box => !targetSet.has(box));
        let position;
        if (isDefined(insertIndex)) {
            position = clamp(insertIndex, 0, existing.length);
        }
        else {
            const maxOrder = targets.reduce((max, box) => Math.max(max, AudioUnitOrdering[box.type.getValue()] ?? 0), 0);
            position = existing.findIndex(box => (AudioUnitOrdering[box.type.getValue()] ?? 0) > maxOrder);
            if (position === -1) {
                position = existing.length;
            }
        }
        const ordered = [...existing.slice(0, position), ...targets, ...existing.slice(position)];
        ordered.forEach((box, index) => box.index.setValue(index));
    };
    TransferUtils.extractRegions = (regionBoxes, { boxGraph, mandatoryBoxes: { primaryAudioBusBox, rootBox } }, insertPosition = 0.0) => {
        assert(Arrays.satisfy(regionBoxes, isSameGraph), "Region smust be from the same BoxGraph");
        const regionBoxSet = new Set(regionBoxes);
        const trackBoxSet = new Set();
        const audioUnitBoxSet = new SetMultimap();
        regionBoxes.forEach(regionBox => {
            const trackBox = asInstanceOf(regionBox.regions.targetVertex.unwrap().box, TrackBox);
            trackBoxSet.add(trackBox);
            const audioUnitBox = asInstanceOf(trackBox.tracks.targetVertex.unwrap().box, AudioUnitBox);
            audioUnitBoxSet.add(audioUnitBox, trackBox);
        });
        console.debug(`Found ${audioUnitBoxSet.keyCount()} audioUnits`);
        console.debug(`Found ${trackBoxSet.size} tracks`);
        const audioUnitBoxes = [...audioUnitBoxSet.keys()];
        const excludeBox = (box) => TransferUtils.shouldExclude(box)
            || (isInstanceOf(box, TrackBox) && !trackBoxSet.has(box))
            || (UnionBoxTypes.isRegionBox(box) && !regionBoxSet.has(box));
        const dependencies = Array.from(audioUnitBoxes[0].graph.dependenciesOf(audioUnitBoxes, {
            alwaysFollowMandatory: true,
            stopAtResources: true,
            excludeBox
        }).boxes);
        const uuidMap = TransferUtils.generateMap(audioUnitBoxes, dependencies, rootBox.audioUnits.address.uuid, primaryAudioBusBox.address.uuid);
        TransferUtils.copyBoxes(uuidMap, boxGraph, audioUnitBoxes, dependencies);
        TransferUtils.reorderAudioUnits(uuidMap, audioUnitBoxes, rootBox);
        audioUnitBoxSet.forEach((_, trackBoxes) => [...trackBoxes]
            .sort(compareIndex)
            .forEach((source, index) => {
            const box = boxGraph
                .findBox(uuidMap.get(source.address.uuid).target)
                .unwrap("Target Track has not been copied");
            asInstanceOf(box, TrackBox).index.setValue(index);
        }));
        const minPosition = regionBoxes.reduce((min, region) => Math.min(min, region.position.getValue()), Number.MAX_VALUE);
        const delta = insertPosition - minPosition;
        regionBoxes.forEach((source) => {
            const box = boxGraph
                .findBox(uuidMap.get(source.address.uuid).target)
                .unwrap("Target Track has not been copied");
            const { position } = UnionBoxTypes.asRegionBox(box);
            position.setValue(position.getValue() + delta);
        });
    };
})(TransferUtils || (TransferUtils = {}));
